import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Select, Tabs } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { relativeTime } from '@/lib/format';
import { listLeads, loadSampleLeads } from '@/data/client';
import type { DealStatus, Lead, LeadStatus, SourceBucket } from '@/data/types';
import { useMe } from '@/auth/useMe';
import { useSearch } from '@/components/shell/search';
import { BUCKET_OPTIONS, SourceBadge } from '@/components/crm/SourceBadge';
import { LeadDrawer } from '@/components/crm/LeadDrawer';
import { NewLeadModal } from '@/components/crm/NewLeadModal';

const TABS: { value: LeadStatus | 'all'; label: string }[] = [
  { value: 'new', label: 'Inbox' }, { value: 'linked', label: 'On a deal' }, { value: 'dismissed', label: 'Dismissed' }, { value: 'all', label: 'All' },
];
const DEAL_FILTER: { value: DealStatus | ''; label: string }[] = [
  { value: 'open', label: 'Open deals' }, { value: 'won', label: 'Won deals' }, { value: 'lost', label: 'Lost deals' }, { value: '', label: 'All deals' },
];

/* The leads inbox. Every new lead — leadIQ or manual — waits here until a rep
   starts a deal, links it to one, or dismisses it. Nothing leaves silently.
   "On a deal" opens on open deals: a lost deal keeps its lead (it is still
   where that deal came from) but isn't something to work. */
export function Leads() {
  const qc = useQueryClient();
  const { me } = useMe();
  const { query } = useSearch();
  const [status, setStatus] = useState<LeadStatus | 'all'>('new');
  const [bucket, setBucket] = useState<SourceBucket | ''>('');
  const [deal, setDeal] = useState<DealStatus | ''>('open');
  const dealFilter = status === 'linked' ? deal : '';
  const [openId, setOpenId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['leads', { status, bucket, q: query, deal: dealFilter }],
    queryFn: () => listLeads({ status, bucket, q: query, deal: dealFilter }),
    placeholderData: (prev) => prev,
  });
  const sample = useMutation({
    mutationFn: loadSampleLeads,
    onSuccess: () => { for (const k of ['leads', 'leadSummary', 'companies', 'contacts']) void qc.invalidateQueries({ queryKey: [k] }); },
  });

  return (
    <div className="crm-list">
      <div className="lead-tabsbar">
        <Tabs items={TABS} value={status} onChange={(v) => setStatus(v as LeadStatus | 'all')} />
      </div>
      <div className="lead-toolbar">
        <div className="lead-toolbar__filters">
          <label className="lead-field">
            <Icon name="filter" size={16} />
            <Select aria-label="Source" options={[{ value: '', label: 'All sources' }, ...BUCKET_OPTIONS]} value={bucket} onChange={(e) => setBucket(e.target.value as SourceBucket | '')} />
          </label>
          {status === 'linked' && (
            <label className="lead-field">
              <Select aria-label="Deal status" options={DEAL_FILTER} value={deal} onChange={(e) => setDeal(e.target.value as DealStatus | '')} />
            </label>
          )}
        </div>
        <div className="lead-toolbar__right">
          {me?.devTools && me.isAdmin && (
            <Button variant="ghost" onClick={() => sample.mutate()} disabled={sample.isPending}>
              {sample.isPending ? 'Loading…' : 'Load sample leads'}
            </Button>
          )}
          <Button iconLeft={<Icon name="plus" size={16} />} onClick={() => setAdding(true)}>Add lead</Button>
        </div>
      </div>

      <Card padding={false}>
        <table className="dash-table lead-table">
          <thead><tr><th>Received</th><th>Lead</th><th>Source</th><th>Channel</th><th className="crm-num">Score</th><th>Status</th></tr></thead>
          <tbody>
            {data?.map((l) => (
              <tr key={l.id} className={l.status === 'new' ? 'crm-row--new' : ''} onClick={() => setOpenId(l.id)}>
                <td className="crm-cell-muted" title={l.receivedAt}>{relativeTime(l.receivedAt)}</td>
                <td><strong>{l.name}</strong><span className="crm-cell-sub">{l.companyName}</span></td>
                <td><SourceBadge lead={l} /></td>
                <td className="crm-cell-muted">{l.channel ?? '—'}</td>
                <td className="crm-num">{l.aiQualityScore ?? '—'}</td>
                <td className="crm-cell-muted"><LeadStatusCell lead={l} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {!isLoading && !data?.length && (
          <p className="lead-none">{status === 'new' ? 'Inbox zero — no new leads.' : 'No leads here.'}</p>
        )}
      </Card>

      {openId && <LeadDrawer leadId={openId} onClose={() => setOpenId(null)} />}
      {adding && <NewLeadModal onClose={() => setAdding(false)} />}
    </div>
  );
}

function LeadStatusCell({ lead }: { lead: Lead }) {
  if (lead.status === 'dismissed') return <>Dismissed</>;
  if (lead.status !== 'linked') return <>New</>;
  const state = lead.dealStatus === 'won' ? 'Won' : lead.dealStatus === 'lost' ? 'Lost' : lead.dealStageName;
  return <>{lead.dealName}{state && <span className="crm-cell-sub">{state}</span>}</>;
}
